import {
  forwardRef,
  HTMLAttributes,
  InputHTMLAttributes,
  memo,
  useEffect, useId,
  useRef,
  useState
} from "react";
import classNames from "classnames";
import { InputContainerState } from "../../InputContainer";

import styles from "./style.module.scss";

export type VerificationInputBaseProps = {
  value?: string,
  validChars?: string,
  placeholder?: string,
  autoFocus?: boolean,
  passwordMode?: boolean,
  inputProps?: InputHTMLAttributes<HTMLInputElement>,
  containerProps?: HTMLAttributes<HTMLDivElement>,
  classNames?: {
    container?: string,
    character?: string,
    characterInactive?: string,
    characterSelected?: string,
    characterFilled?: string,
  },
  onChange?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onComplete?: (value: string) => void;
  disabled?:boolean,
  pattern: string; // fe. '..-..' or '.-..-..-.' and more...
  inputState?: InputContainerState;
};

export const VerificationInputBase = memo(
  forwardRef<HTMLDivElement, VerificationInputBaseProps>(
    (
      {
        value,
        validChars = "A-Za-z0-9",
        placeholder = "",
        autoFocus = false,
        passwordMode,
        inputProps,
        containerProps,
        classNames: classes,
        onChange,
        onFocus,
        onBlur,
        onComplete,
        disabled,
        pattern,
        inputState,
      },
      ref,
    ) => {
      const [ localValue, setLocalValue ] = useState("");
      const [ isActive, setActive ] = useState(false);

      const inputRef = useRef<HTMLInputElement>(null);

      const patternLength = pattern.split('-').join('').length

      const idHook = useId()
      const internalId = inputProps?.id || idHook

      useEffect(() => {
        if (autoFocus) {
          inputRef.current?.focus();
        }
      }, [ autoFocus ]);

      const handleClick = () => {
        inputRef.current?.focus();
      };

      const wrappedValue = value ?? localValue

      const isCharacterSelected = (i:number) => {
        return (
          (wrappedValue.length === i || (wrappedValue.length === i + 1 && patternLength === i + 1)) &&
          isActive
        );
      };

      const isCharacterFilled = (i: number) => wrappedValue.length > i;

      return (
        <>
          <div
            onClick={() => inputRef.current?.focus()}
            {...containerProps}
            className={classNames(
              styles.container,
              classes?.container,
              styles[inputState as keyof typeof styles],
              containerProps?.className,
              {
                [styles.disabled]: disabled,
              },
            )}
            ref={ref}
          >
            <input
              spellCheck={false}
              value={wrappedValue}
              onChange={(event) => {
                const newInputVal = event.target.value.replace(/\s/g, "");

                if (
                  RegExp(`^[${validChars}]{0,${patternLength}}$`).test(newInputVal)
                ) {
                  if (onChange) {
                    onChange?.(newInputVal);
                  }
                  setLocalValue(newInputVal);

                  if (newInputVal.length === patternLength) {
                    onComplete?.(newInputVal);
                  }
                }
              }}
              ref={inputRef}
              onKeyDown={(event) => {
                if (
                  [
                    "ArrowLeft",
                    "ArrowRight",
                    "ArrowUp",
                    "ArrowDown",
                  ].includes(event.key)
                ) {
                  event.preventDefault();
                }
              }}
              onFocus={() => {
                setActive(true);
                onFocus?.();
              }}
              onBlur={() => {
                setActive(false);
                onBlur?.();
              }}
              onSelect={(e) => {
                const target = e.target as HTMLInputElement;
                const val = target.value;
                target.setSelectionRange(val.length, val.length);
              }}
              {...inputProps}
              className={classNames(
                styles.containerInput,
                inputProps?.className,
              )}
              type={passwordMode ? "password" : inputProps?.type}
            />
            {pattern?.split("").map((patEl, i, array) => {

              const dashCount = array.slice(0, i).filter((el)=> el === "-").length
              const key = i - dashCount;

              return (
                patEl === "-" ? (
                  <span className={classNames(styles.slicer)} key={i}>-</span>
                ) : (
                  <div
                    className={classNames(
                      styles.character,
                      classes?.character,
                      {
                        [styles.selected]: isCharacterSelected(key),
                        [styles.filled]: isCharacterFilled(key),
                      },
                      isCharacterSelected(key) && isActive && classes?.characterSelected,
                      isCharacterFilled(key) && classes?.characterFilled,
                    )}
                    onClick={handleClick}
                    id={`${internalId}--field-${key}`}
                    key={i}
                  >
                    {passwordMode && (value ?? localValue)[key]
                      ? "*"
                      : wrappedValue[key] ||
                      (disabled ? "0" : placeholder)}
                  </div>
                )
              )
            })}
          </div>
        </>
      );
    },
  ),
);
